/**
 * DPL Jade — CAD editor orchestrator.
 * Composes useCADState + useCADHandlers + layout components.
 */
import React from "react";
import { useCADState }    from "../hooks/useCADState";
import { useCADHandlers } from "../hooks/useCADHandlers";
import {
  mutHeal, mutDeleteEdge, mutDeleteOpening, mutSetProject,
} from "../engine/mutations";
import { mkEmpty } from "../engine/dcel";
import { downloadDXF, exportDXFWithPrimitives } from "../engine/dxfExport";
import { downloadGCode, downloadCutList } from "../engine/cncEngine";
import { wallSchedule, openingSchedule, roomSchedule, fullBOQ } from "../engine/scheduleExport";
import Header          from "./Header";
import PrintView       from "./PrintView";
import ProjectEditor   from "./modals/ProjectEditor";
import HealLogPanel    from "./modals/HealLogPanel";
import TemplateModal   from "./modals/TemplateModal";
import DrawingView     from "./DrawingView";
import CADCanvas       from "./cad/CADCanvas";
import LayerPanelCAD   from "./cad/LayerPanelCAD";
import PropertiesPanel from "./cad/PropertiesPanel";
import CommandLine     from "./cad/CommandLine";
import MenuBar         from "./cad/MenuBar";
import ThreeDView      from "./ThreeDView";
import AIPanel         from "./ai/AIPanel";

const CAD_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;700&display=swap');
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  :root {
    --bg:#020a14; --bg2:#04080f; --bg3:#06111e;
    --border:#0d1520; --border2:#131924;
    --text:#6b7e94; --text2:#4a5e70; --text3:#2a3a4a;
    --accent:#3b82f6; --amber:#e8a838; --green:#4ade80; --red:#f87171;
    --mono:'JetBrains Mono','Fira Code',monospace;
  }
  body { background:var(--bg); font-family:var(--mono); }
  svg  { display:block; }
  ::-webkit-scrollbar { width:4px; height:4px; }
  ::-webkit-scrollbar-track { background:transparent; }
  ::-webkit-scrollbar-thumb { background:var(--border2); border-radius:2px; }
  ::-webkit-scrollbar-thumb:hover { background:var(--border); }
  select,input[type=text],input[type=number],input[type=email] {
    background:var(--bg2); border:1px solid var(--border);
    color:var(--text); border-radius:2px; padding:2px 6px;
    font-size:9px; font-family:var(--mono); outline:none;
    transition:border-color .1s;
  }
  select:focus,input:focus { border-color:var(--accent); }
  input[type=checkbox] { accent-color:var(--accent); }
  input[type=color]    { padding:0; border:none; cursor:pointer; }
  button               { font-family:var(--mono); }
  @keyframes fadeIn  { from{opacity:0;transform:translateY(3px)}  to{opacity:1;transform:none} }
  @keyframes slideIn { from{opacity:0;transform:translateX(-6px)} to{opacity:1;transform:none} }
  #pc-print { display:none; }
  @media print { #pc-app{display:none!important;} #pc-print{display:block!important;} }
`;

export default function DPLJade() {
  const cad      = useCADState();
  const handlers = useCADHandlers(cad);
  const { handleCommand: _handleCommand, handleResetOrigin } = handlers;
  // Intercept commands that need DPLJade-level state (show3D etc.)
  const handleCommand = React.useCallback(cmd => {
    if (cmd.type === "TOGGLE_3D") { setShow3D(v => !v); return; }
    _handleCommand(cmd);
  }, [_handleCommand]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    st, commit, undo, redo, canUndo, canRedo, beginDrag, commitDrag,
    user, plan, isPro, saveStatus,
    hydrated,
    svgRef,
    pan, zoom, setZoom,
    mode, setMode, matId, setMatId, wallH, setWallH,
    wStart, snap, dragNode, setDragNode, dragPosMm, setDragPosMm,
    sel, setSel, clearSel, selAnnotation, setSelAnnotation,
    showHE, setShowHE,
    editingDim, setEditingDim,
    roomPopup,    setRoomPopup,
    openingPopup, setOpeningPopup,
    showHealLog,       setShowHealLog,
    showProjectEditor, setShowProjectEditor,
    showTemplates,     setShowTemplates,
    showDrawing,       setShowDrawing,
    showLayerPanel,    setShowLayerPanel,
    activeLayer,       setActiveLayer,
    activeSymbol,      setActiveSymbol,
    pendingAnnotation, setPendingAnnotation,
    pendingGridLine,   setPendingGridLine,
    annotationDraft,   setAnnotationDraft,
    isFullscreen,      setIsFullscreen,
    showCmdLine,       setShowCmdLine,
    showMenuBar,       setShowMenuBar,
    activeMenu,        setActiveMenu,
    unitPreset,        setUnitPreset,
    svgSize, boq, errors, hasErr,
  } = cad;

  // ── Transform state from cad ──────────────────────────────────────────────
  const {
    offsetDist, setOffsetDist, offsetSide, setOffsetSide, offsetHoverEdge, setOffsetHoverEdge,
    selMany, setSelMany, selBox, setSelBox, selBoxAnchor, setSelBoxAnchor,
    dragNodes, setDragNodes, dragAnchor, setDragAnchor, dragDelta, setDragDelta,
    dragSymbol, setDragSymbol, dragSymPos, setDragSymPos,
    dragAnn, setDragAnn, dragAnnPos, setDragAnnPos,
    snapLock, setSnapLock, snapRef,
    panRef, isPanning, lastClick, setPan,
    activeSectionCut, setActiveSectionCut,
    tab, setTab, plan: _plan, saveStatus: _save,
    transformAngle, setTransformAngle,
    transformScale, setTransformScale,
    arrayRows, setArrayRows,
    arrayCols, setArrayCols,
    arrayRowSpacing, setArrayRowSpacing,
    arrayColSpacing, setArrayColSpacing,
    arrayCount, setArrayCount,
    arrayTotalAngle, setArrayTotalAngle,
    mirrorAxisStart, setMirrorAxisStart,
    showTransformPanel, setShowTransformPanel,
    adimPts, setAdimPts,
    trimTarget, setTrimTarget,
    primDrawPts, setPrimDrawPts,
    primDrawType, setPrimDrawType,
    selPrim, setSelPrim,
    grid,
  } = cad;

  // ── Panel toggles ──────────────────────────────────────────────────────────
  const [showProps, setShowProps] = React.useState(true);
  const [show3D,    setShow3D]    = React.useState(false);
  const [showAI,    setShowAI]    = React.useState(false);

  // ── Zoom helpers ──────────────────────────────────────────────────────────
  const handleZoomIn  = () => setZoom(z => Math.min(6, z * 1.25));
  const handleZoomOut = () => setZoom(z => Math.max(0.1, z * 0.8));
  const handleZoomFit = () => {
    const nodes = Object.values(st.nodes || {});
    if (!nodes.length) { setZoom(1); return; }
    const xs = nodes.map(n => n.x), ys = nodes.map(n => n.y);
    const w = Math.max(...xs) - Math.min(...xs) || 1000;
    const h = Math.max(...ys) - Math.min(...ys) || 1000;
    const vw = svgSize.w - 120, vh = svgSize.h - 80;
    setZoom(Math.min(vw / (w * 0.2), vh / (h * 0.2), 4) * 0.85);
  };

  // ── Fullscreen ────────────────────────────────────────────────────────────
  React.useEffect(() => {
    if (isFullscreen) {
      document.documentElement.requestFullscreen?.().catch(() => {});
    } else {
      if (document.fullscreenElement) document.exitFullscreen?.();
    }
  }, [isFullscreen]);

  // ── Clear selection when drawing mode (tool) changes ───────────────────────
  React.useEffect(() => {
    clearSel();
  }, [mode]);

  // ── 3D shortcut key ───────────────────────────────────────────────────────
  React.useEffect(() => {
    const handler = e => {
      if (["INPUT","TEXTAREA"].includes(e.target.tagName)) return;
      if (e.key === "3") setShow3D(v => !v);
      if ((e.key === "j" || e.key === "J") && (e.ctrlKey || e.metaKey)) {
        e.preventDefault(); setShowAI(v => !v); // Ctrl+J = AI panel
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // ── Loading screen ────────────────────────────────────────────────────────
  if (!hydrated) return (
    <div style={{ width:"100vw", height:"100vh", background:"#020a14",
      display:"flex", alignItems:"center", justifyContent:"center",
      fontFamily:"'JetBrains Mono',monospace" }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontSize:8, color:"#1e2a36", letterSpacing:3, marginBottom:10 }}>J.A.D.E</div>
        <div style={{ width:100, height:1, background:"#0d1520", overflow:"hidden" }}>
          <div style={{ height:"100%", background:"linear-gradient(90deg,#3b82f6,#e8a838)",
            animation:"pc-load 1s ease-in-out infinite" }}/>
        </div>
        <style>{`@keyframes pc-load{0%,100%{transform:translateX(-100%)}50%{transform:translateX(100%)}}`}</style>
      </div>
    </div>
  );

  return (
    <div id="pc-app" style={{
      width:"100vw", height:"100vh", display:"flex", flexDirection:"column",
      background:"#020a14", fontFamily:"'JetBrains Mono','Fira Code',monospace",
      overflow:"hidden", position:"relative", color:"#6b7e94",
    }}>
      <style>{CAD_CSS}</style>

      {/* ── Menu bar ─────────────────────────────────────────────────────── */}
      {showMenuBar && (
        <MenuBar
          activeMenu={activeMenu} setActiveMenu={setActiveMenu}
          onNew={() => { commit(() => mkEmpty()); clearSel(); }}
          onSave={() => {}}
          onPrint={() => window.print()}
          onExportSVG={() => {
            const el = document.querySelector("#pc-drawing-export svg");
            if (el) {
              const blob = new Blob([el.outerHTML], { type:"image/svg+xml" });
              const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
              a.download = "dpl-jade-export.svg"; a.click();
            }
          }}
          onExportDXF={() => downloadDXF(st, `${st.project?.name || "dpl-jade"}.dxf`)}
          onExportDXFFull={() => {
            const dxf = exportDXFWithPrimitives(st);
            const blob = new Blob([dxf], { type:"application/dxf" });
            const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
            a.download = `${st.project?.name || "dpl-jade"}-full.dxf`; a.click();
            URL.revokeObjectURL(a.href);
          }}
          onExportWallSchedule={() => wallSchedule(st)}
          onExportOpeningSchedule={() => openingSchedule(st)}
          onExportRoomSchedule={() => roomSchedule(st)}
          onExportBOQ={() => fullBOQ(st)}
          onExportCutList={() => downloadCutList(st)}
          onExportGCodeRouter={() => downloadGCode(st, "router", { kerf: true, materialThickness: 18 })}
          onExportGCodeLaser={() => downloadGCode(st, "laser", { kerf: true, materialThickness: 6 })}
          onToggle3D={() => setShow3D(v => !v)}
          show3D={show3D}
          onToggleAI={() => setShowAI(v => !v)}
          showAI={showAI}
          onUndo={undo} onRedo={redo}
          onClear={() => { commit(() => mkEmpty()); clearSel(); }}
          onSelectAll={() => handlers.handleCommand({ type: "SELECT_ALL" })}
          onToggleLayers={() => setShowLayerPanel(v => !v)}
          onToggleProperties={() => setShowProps(v => !v)}
          onToggleCommandLine={() => setShowCmdLine(v => !v)}
          onToggleMenuBar={() => setShowMenuBar(false)}
          onFullscreen={() => setIsFullscreen(v => !v)}
          onZoomFit={handleZoomFit}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onTemplates={() => setShowTemplates(true)}
          onDrawing={() => setShowDrawing(true)}
          onHeal={() => commit(mutHeal)}
          onProjectEdit={() => setShowProjectEditor(true)}
          onDrawWall={() => setMode("wall")}
          onDrawLine={() => setMode("wall")}
          onDrawRoom={() => setMode("room")}
          onDrawOpening={() => setMode("opening")}
          onDrawAnnotation={() => setMode("annotate")}
          onDrawDimension={() => setMode("dim")}
          onDrawGridLine={() => setMode("wall")}
          showLayerPanel={showLayerPanel}
          showCmdLine={showCmdLine}
          isFullscreen={isFullscreen}
        />
      )}

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <Header
        project={st.project || { name:"Untitled" }}
        saveStatus={saveStatus}
        healLog={st._healLog}
        onHealOpen={() => setShowHealLog(v => !v)}
        onProjectEdit={() => setShowProjectEditor(true)}
        boq={boq} user={user} isPro={isPro} plan={plan}
        showLayerPanel={showLayerPanel} onToggleLayers={() => setShowLayerPanel(v => !v)}
        showProps={showProps}           onToggleProps={() => setShowProps(v => !v)}
        showCmdLine={showCmdLine}       onToggleCmdLine={() => setShowCmdLine(v => !v)}
      />

      {/* ── Main row ─────────────────────────────────────────────────────── */}
      <div style={{ display:"flex", flex:1, overflow:"hidden" }}>

        {showLayerPanel && (
          <LayerPanelCAD
            st={st} commit={commit}
            sel={sel} selAnnotation={selAnnotation}
            activeSymbol={activeSymbol}
            onPickSymbol={symId => setActiveSymbol(symId)}
            activeLayer={activeLayer}
            onSetActiveLayer={setActiveLayer}
            onSelectObject={obj => {
              clearSel();
              if (obj.type === "edge")   setSel(p => ({ ...p, edge:   obj.id }));
              if (obj.type === "face")   setSel(p => ({ ...p, face:   obj.id }));
              if (obj.type === "symbol") setSel(p => ({ ...p, symbol: obj.id }));
              if (["ann","dim","gl"].includes(obj.type)) setSelAnnotation(obj.id);
            }}
          />
        )}

        <CADCanvas
          st={st} snap={snap} svgSize={svgSize} pan={pan} zoom={zoom}
          mode={mode} matId={matId} setMatId={setMatId}
          wallH={wallH} setWallH={setWallH}
          wStart={wStart} sel={sel} setSel={setSel}
          selAnnotation={selAnnotation} setSelAnnotation={setSelAnnotation}
          dragNode={dragNode} setDragNode={setDragNode}
          dragPosMm={dragPosMm} setDragPosMm={setDragPosMm}
          dragNodes={dragNodes} setDragNodes={setDragNodes}
          dragAnchor={dragAnchor} setDragAnchor={setDragAnchor}
          dragDelta={dragDelta} setDragDelta={setDragDelta}
          beginDrag={beginDrag} commitDrag={commitDrag}
          showHE={showHE} showLayerPanelState={showLayerPanel}
          activeSymbol={activeSymbol} setActiveSymbol={setActiveSymbol}
          editingDim={editingDim} setEditingDim={setEditingDim}
          roomPopup={roomPopup} setRoomPopup={setRoomPopup}
          openingPopup={openingPopup} setOpeningPopup={setOpeningPopup}
          pendingAnnotation={pendingAnnotation} setPendingAnnotation={setPendingAnnotation}
          pendingGridLine={pendingGridLine} setPendingGridLine={setPendingGridLine}
          annotationDraft={annotationDraft} setAnnotationDraft={setAnnotationDraft}
          unitPreset={unitPreset}
          offsetDist={offsetDist} setOffsetDist={setOffsetDist}
          offsetHoverEdge={offsetHoverEdge} offsetSide={offsetSide}
          selMany={selMany} setSelMany={setSelMany}
          selBox={selBox}
          dragSymbol={dragSymbol} setDragSymbol={setDragSymbol}
          dragSymPos={dragSymPos} setDragSymPos={setDragSymPos}
          dragAnn={dragAnn} setDragAnn={setDragAnn}
          dragAnnPos={dragAnnPos} setDragAnnPos={setDragAnnPos}
          hasErr={hasErr} errCount={errors.length}
          commit={commit} clearSel={clearSel}
          svgRef={svgRef}
          onMM={handlers.onMM} onMD={handlers.onMD}
          onMU={handlers.onMU} onWh={handlers.onWh}
          onSvgClick={handlers.onSvgClick} onSvgDbl={handlers.onSvgDbl}
          onSetMode={m => setMode(m)}
          onTemplates={() => setShowTemplates(true)}
          onDrawing={() => setShowDrawing(true)}
          onHeal={() => commit(mutHeal)}
          onToggleLayers={() => setShowLayerPanel(v => !v)}
          showTransformPanel={showTransformPanel}
          onToggleTransform={() => setShowTransformPanel(v => !v)}
          show3D={show3D}
          onToggle3D={() => setShow3D(v => !v)}
          showAI={showAI}
          onToggleAI={() => setShowAI(v => !v)}
          transformAngle={transformAngle}   setTransformAngle={setTransformAngle}
          transformScale={transformScale}   setTransformScale={setTransformScale}
          arrayRows={arrayRows}             setArrayRows={setArrayRows}
          arrayCols={arrayCols}             setArrayCols={setArrayCols}
          arrayRowSpacing={arrayRowSpacing} setArrayRowSpacing={setArrayRowSpacing}
          arrayColSpacing={arrayColSpacing} setArrayColSpacing={setArrayColSpacing}
          arrayCount={arrayCount}           setArrayCount={setArrayCount}
          arrayTotalAngle={arrayTotalAngle} setArrayTotalAngle={setArrayTotalAngle}
          mirrorAxisStart={mirrorAxisStart}
          adimPts={adimPts}
          trimTarget={trimTarget}
          primDrawPts={primDrawPts}
          selPrim={selPrim}
          drawingMode={st.drawingMode || "architectural"}
          onSetDrawingMode={mode => commit(s => ({ ...s, drawingMode: mode }))}
          onCommand={handleCommand}
        />

        {showProps && (
          <PropertiesPanel
            st={st} sel={sel} selAnnotation={selAnnotation}
            commit={commit} selMany={selMany}
            unitPreset={st.unitPreset}
            mode={mode}
            onDeleteEdge={eId => { commit(s => mutDeleteEdge(eId, s)); clearSel(); }}
            onDeleteOpening={opId => { commit(s => mutDeleteOpening(opId, s)); clearSel(); }}
            onResetOrigin={handleResetOrigin}
            showHE={showHE} setShowHE={setShowHE}
            canUndo={canUndo} canRedo={canRedo}
            onUndo={undo} onRedo={redo}
            onClear={() => { commit(() => mkEmpty()); clearSel(); }}
          />
        )}

        {/* ── 3D preview overlay ──────────────────────────────────────────── */}
        {show3D && (
          <ThreeDView st={st} onClose={() => setShow3D(false)}/>
        )}

        {/* ── AI assistant panel ───────────────────────────────────────────── */}
        {showAI && (
          <AIPanel
            st={st}
            commit={commit}
            user={user}
            onClose={() => setShowAI(false)}
          />
        )}
      </div>

      {/* ── Command line ─────────────────────────────────────────────────── */}
      {showCmdLine && (
        <CommandLine
          onCommand={handleCommand}
          snapPoint={snap?.point}
          mode={mode}
          zoom={zoom}
        />
      )}

      {/* ── Modals ───────────────────────────────────────────────────────── */}
      {showDrawing    && <DrawingView st={st} commit={commit} onClose={() => setShowDrawing(false)}/>}
      {showTemplates  && <TemplateModal onApply={s => { commit(() => s); setShowTemplates(false); }} onClose={() => setShowTemplates(false)}/>}
      {showHealLog    && <HealLogPanel healLog={st._healLog} onClose={() => setShowHealLog(false)}/>}
      {showProjectEditor && (
        <ProjectEditor
          project={st.project || { name:"Untitled" }}
          onSave={fields => { commit(s => mutSetProject(fields, s)); setShowProjectEditor(false); }}
          onClose={() => setShowProjectEditor(false)}
        />
      )}

      <PrintView st={st} boq={boq}/>
    </div>
  );
}
